import i18n from 'i18next';
import { createContext, useContext, useState } from "react"
import endpoints from "services/apis/gameplay"
import AuthApi from "services/apis/auth"
import { storage } from "services/config/storage"
import { LOGIN_TYPE, ORG_SET_EVENT } from "constants/globalConstants"

const GameplayConfigContext = createContext(null)

export const GameplayConfigProvider = ({ children }) => {

    const [allowMobileView, setAllowMobileView] = useState(true)
    const [isProctored, setIsProctored] = useState(false)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const [tabSwitchLimit, setTabSwitchLimit] = useState(Number.MAX_SAFE_INTEGER)
    const [isFeedbackVisible, setIsFeedbackVisible] = useState(true)
    const [snapshotInterval, setSnapshotInterval] = useState(60)
    const [isVideoProctored, setIsVideoProctored] = useState(false)
    const [isLoading, setIsloading] = useState(true)
    const [positionId, setPositionId] = useState("")
    const [customizableForm, setCustomizableForm] = useState(true)
    const [instructionsContent, setInstructionsContent] = useState({
        heading: null,
        content: null,
        proctoringNotes: null,
    })
    const [configLanguageSettings, setConfigLanguageSettings] = useState({
        configLanguage: 'en',
        isRtlLayout: false
    });
    const [languagesOptions, setLanguagesOptions] = useState([])
    const [isScreenProctored, setIsScreenProctored] = useState(false)
    const [isAudioProctored, setIsAudioProctored] = useState(false)
    const [candidateInfo, setCandidateInfo] = useState(null)

    async function fetchPositionConfig(idType, id) {
        try {
            const configRes = await endpoints.getGameplayConfig(idType, id)
            return configRes
        } catch (err) {
            console.error('Error in fetching gameplay config :: ', err)
        } finally {
            storage.destroy.configStat()
        }
    }

    async function fetchLinkConfig(id) {
        try {
            const configRes = await endpoints.getLinkPositionConfig(id)
            return configRes
        } catch (err) {
            console.error('Error in fetching gameplay config :: ', err)
        } finally {
        }
    }

    async function fetchConfig(showLoader = true) {
        let positionConfigRes, linkConfigRes
        try {
            const { pathname } = window.location
            const id = pathname.split('/').pop()
            const page = pathname.split('/')[1]
            const idType = ["login", "signup"].includes(page) ? "positionId" : "linkId"
            if (id && (page !== "system-check" && page !== "report" && page !== "randomizer")) {
                if (showLoader) {
                    setIsloading(true)
                }
                [positionConfigRes, linkConfigRes] = await Promise.allSettled([
                    fetchPositionConfig(idType, id),
                    fetchLinkConfig(id)
                ])
                if (positionConfigRes?.value?.success || linkConfigRes?.value?.success) {
                    const { mobile,
                        proctoring,
                        fullScreen,
                        maxTabSwitch,
                        feedback,
                        loginType,
                        positionId,
                        saveProctoredVideo,
                        snapshotTimeInterval,
                        organization,
                        language,
                        customizableForm,
                        instructionHeading,
                        instructionHtml,
                        proctoringNotes,
                        screenProctoring,
                        audioProctoring, } = positionConfigRes?.value?.positionConfig ?? {}
                    const { media } = positionConfigRes?.value?.positionConfig?.organization ?? {}
                    setPositionId(positionId)
                    setAllowMobileView(!!mobile)
                    if (linkConfigRes?.value?.linkPositionConfig) {
                        setIsProctored(!!linkConfigRes?.value?.linkPositionConfig?.proctoring)
                        setIsVideoProctored(!!linkConfigRes?.value?.linkPositionConfig?.saveProctoredVideo)
                    } else {
                        setIsProctored(!!proctoring)
                        setIsVideoProctored(!!saveProctoredVideo)
                    }
                    setIsFullScreen(!!fullScreen)
                    setIsFeedbackVisible(!!feedback)
                    setSnapshotInterval(snapshotTimeInterval)
                    setCustomizableForm(customizableForm)
                    setInstructionsContent({
                        heading: instructionHeading,
                        content: instructionHtml,
                        proctoring: proctoringNotes,
                    })
                    setIsScreenProctored(screenProctoring)
                    setIsAudioProctored(!!audioProctoring)
                    if (language) {
                        const languages = language.split(",")
                        let languageToSet = languages[0]
                        const prevSelectedLang = storage.get.userLang()
                        if (prevSelectedLang && languages.includes(prevSelectedLang)) {
                            languageToSet = prevSelectedLang
                        }
                        if (languages.length > 1) {
                            setLanguagesOptions(languages)
                        }
                        const isRtlLayout = languageToSet === 'ar' ? true : false
                        setConfigLanguageSettings({
                            configLanguage: languageToSet,
                            layoutDirection: isRtlLayout ? 'rtl' : 'ltr',
                            isRtlLayout
                        })
                        i18n.changeLanguage(languageToSet)
                        storage.set.userLang(languageToSet)
                    }
                    if (maxTabSwitch) {
                        setTabSwitchLimit(maxTabSwitch)
                    }
                    if (loginType) {
                        storage.set.loginType(loginType)
                        if (loginType === LOGIN_TYPE.EMAIL || loginType === LOGIN_TYPE.PHONE) {
                            try {
                                const candidateInfoRes = await AuthApi.getCandidateInfo(id)
                                if (candidateInfoRes?.success) {
                                    setCandidateInfo(candidateInfoRes.candidateInfo)
                                }
                            } catch (err) {
                                console.error('Error fetching candidate info ::', err)
                            }
                        }
                    }
                    if (media?.link) {
                        storage.set.companiesLogos(media?.link ?? "")
                    } else {
                        storage.destroy.companiesLogos()
                    }
                    if (organization) {
                        storage.set.orgInfo(organization);
                        setTimeout(() => window.dispatchEvent(new Event(ORG_SET_EVENT)), 1000)
                    }
                }
            }
        } catch (err) {
            console.error("Error in fetching configs ::", err)
        } finally {
            if (showLoader) {
                setIsloading(false)
            }
            return { positionConfigRes: positionConfigRes?.value, linkConfigRes: linkConfigRes?.value }
        }
    }

    return (
        <GameplayConfigContext.Provider
            value={{
                allowMobileView,
                isProctored,
                isFullScreen,
                tabSwitchLimit,
                isLoading,
                isFeedbackVisible,
                positionId,
                snapshotInterval,
                isVideoProctored,
                customizableForm,
                configLanguageSettings,
                instructionsContent,
                fetchConfig,
                fetchPositionConfig,
                fetchLinkConfig,
                setIsVideoProctored,
                setIsProctored,
                setIsFeedbackVisible,
                setConfigLanguageSettings,
                languagesOptions,
                isScreenProctored,
                setIsScreenProctored,
                isAudioProctored,
                setIsAudioProctored,
                candidateInfo,
            }}
        >
            {children}
        </GameplayConfigContext.Provider>
    )

}

export const useGameplayConfig = () => useContext(GameplayConfigContext)